import * as React from "react";
import {
  CaretSortIcon,
  ChevronDownIcon,
  DotsHorizontalIcon,
} from "@radix-ui/react-icons";
import {
  ColumnDef,
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { ApiQuery, getUsers } from "@/services/getUsers";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import withAuthorization from "@/core/auth/components/withAuthorization";
import { Role } from "@/core/auth/models/role.model";
import { useDebouncedCallback } from "@/hooks/useDebounceCallback";
import { Skeleton } from "@/components/ui/skeleton";
import { useState } from "react";

type Payment = {
  id: string;
  name: string;
  email: string;
  company: string;
  totalCost: number;
  status: "active" | "inactive" | "pending" | "completed";
};

const columns: ColumnDef<Payment>[] = [
  // {
  //   id: "select",
  //   header: ({ table }) => (
  //     <Checkbox
  //       checked={
  //         (table.getIsAllPageRowsSelected()?true : false) ||
  //         (table.getIsSomePageRowsSelected() && "indeterminate" ? true : false)
  //       }
  //       onCheckedChange={(value: any) =>
  //         table.toggleAllPageRowsSelected(!!value)
  //       }
  //       aria-label="Select all"
  //     />
  //   ),
  //   cell: ({ row }) => (
  //     <Checkbox
  //       checked={row.getIsSelected()}
  //       onCheckedChange={(value: any) => row.toggleSelected(!!value)}
  //       aria-label="Select row"
  //     />
  //   ),
  //   enableSorting: false,
  //   enableHiding: false,
  // },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Name
        <CaretSortIcon className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div>{row.getValue("name") ?? ("-" as React.ReactNode)}</div>
    ),
  },
  {
    accessorKey: "email",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Email
        <CaretSortIcon className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => <div>{row.getValue("email") as React.ReactNode}</div>,
  },
  {
    accessorKey: "company",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Company
        <CaretSortIcon className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => <div>{row.getValue("company") as React.ReactNode}</div>,
  },
  // role

  {
    accessorKey: "role",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Role
        <CaretSortIcon className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => <div>{row.getValue("role") as React.ReactNode}</div>,
  },

  // {
  //   accessorKey: "status",
  //   header: ({ column }) => (
  //     <Button
  //       variant="ghost"
  //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
  //     >
  //       Status
  //       <CaretSortIcon className="ml-2 h-4 w-4" />
  //     </Button>
  //   ),
  //   cell: ({ row }) => {
  //     const status = row.getValue("status");
  //     return (
  //       <div
  //         className={`capitalize ${
  //           status === "active"
  //             ? "text-green-600"
  //             : status === "inactive"
  //             ? "text-red-600"
  //             : status === "pending"
  //             ? "text-yellow-600"
  //             : status === "completed"
  //             ? "text-blue-600"
  //             : ""
  //         }`}
  //       >
  //         {status as React.ReactNode}
  //       </div>
  //     );
  //   },
  // },
  {
    id: "actions",
    enableHiding: false,
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Actions
      </Button>
    ),
    cell: ({ row }) => {
      const payment = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <DotsHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            {/* <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(payment.id)}
            >
              Copy User ID
            </DropdownMenuItem> */}
            <DropdownMenuSeparator />
            <DropdownMenuItem>Edit Employee</DropdownMenuItem>
            <DropdownMenuItem>Delete Employee</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

function UsersTable() {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const { user } = useAuth();

  const [filters, setFilters] = useState<ApiQuery>({
    name: "",
    currentPage: 1,
    pageLimit: 10,
    sort: "createdAt",
    sortOrder: "desc" as "asc" | "desc",
    totalPages: 0,
    totalItems: 0,
  });

  const [debouncedFilters, setDebouncedFilters] = useState<ApiQuery>(filters);

  const syncFilters = useDebouncedCallback((updated: typeof filters) => {
    setDebouncedFilters(() => updated);
  }, 800);

  const users = useQuery({
    queryKey: ["users", debouncedFilters],
    queryFn: async () => {
      console.log("api data debouncedFilters", debouncedFilters);

      const response = await getUsers(debouncedFilters);
      console.log("api data", response);
      const updated: ApiQuery = {
        ...filters,
        totalPages: response.paging?.totalPages ?? 0,
        currentPage: response.paging?.currentPage ?? 0,
        pageLimit: response.paging?.pageLimit ?? 0,
      };
      setFilters(() => updated);

      return {
        ...response,
        data: response.data.map((user: any) => ({
          id: user._id,
          email: user.email,
          role: user.role,
          company: user.company?.name,
          name: user.name,
        })),
      };
    },
    enabled: user?.role === "super-admin",
  });

  const table = useReactTable({
    data: Array.isArray(users.data?.data) ? users.data?.data : [],
    columns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onSortingChange: (sortchanged) => {
      console.log("sorting changes", sortchanged);
    },
    manualPagination: true,
    state: {
      // sorting:filters.sort,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full p-4">
      {
        <>
          <div className="flex items-center py-4">
            <Input
              placeholder="Search by name"
              value={filters.name}
              onChange={(e) => {
                const value = e.target.value;
                const updated: ApiQuery = {
                  ...filters,
                  name: value,
                  currentPage: 1,
                };
                setFilters(() => updated);
                syncFilters(updated);
              }}
              className="max-w-sm"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="ml-auto">
                  Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {users.isPending ? (
            [1, 2, 3, 4].map(() => (
              <div className="flex items-center space-x-4 w-full my-8">
                {/* <Skeleton className="h-12 w-12 rounded-full" /> */}
                <div className="space-y-2 w-full">
                  <Skeleton className="h-4 w-[100%]" />
                  <Skeleton className="h-4 w-[100%]" />
                </div>
              </div>
            ))
          ) : users.isError ? (
            <div>Error: {users.error.message}</div>
          ) : (
            <>
              <div className="rounded-md border bg-card">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          return (
                            <TableHead key={header.id}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </TableHead>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && "selected"}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          No results.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-end space-x-2 p-4">
                {/* <div className="flex-1 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} of{" "}
              {table.getFilteredRowModel().rows.length} user(s) selected.
            </div> */}
                <div className="flex-1 text-sm text-muted-foreground">
                  Page {filters.currentPage} of {filters.totalPages}
                </div>
                <div className="space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const updated: ApiQuery = {
                        ...filters,
                        currentPage: filters.currentPage - 1,
                      };
                      setFilters(() => updated);
                      setDebouncedFilters(() => updated);
                    }}
                    disabled={filters.currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const updated: ApiQuery = {
                        ...filters,
                        currentPage: filters.currentPage + 1,
                      };
                      console.log("updated 0", updated);

                      setFilters(() => updated);
                      console.log("updated 1", updated);

                      setDebouncedFilters(() => updated);
                      console.log("updated 2", updated);
                    }}
                    disabled={filters.currentPage === filters.totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      }
    </div>
  );
}

const AuthorizedUsersTable = withAuthorization(UsersTable, [Role.SuperAdmin]);

export default AuthorizedUsersTable;
